import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Instagram, Linkedin, Facebook, Send } from "lucide-react";
import { EMAIL } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const fieldCls =
  "w-full bg-cardbg border border-line rounded-sm px-4 py-3 text-sm text-ink placeholder:text-ink3 focus:border-brass transition-colors duration-300";

export const ContactSection = ({ t }) => {
  const [form, setForm] = useState({ name: "", email: "", project_type: "production", message: "" });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", project_type: "production", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success(t.contact.copied);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(EMAIL);
    }
  };

  const socials = [
    { id: "instagram", Icon: Instagram, url: "https://instagram.com/thomaslubeau" },
    { id: "linkedin", Icon: Linkedin, url: "https://www.linkedin.com/in/thomas-lubeau-2b1b64a5" },
    { id: "facebook", Icon: Facebook, url: "https://www.facebook.com/thomas.lubeau" },
  ];

  return (
    <section id="contact" className="py-24 sm:py-32 lg:py-40 grain relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <motion.div {...reveal} className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brass mb-6">{t.contact.overline}</p>
            <h2 data-testid="contact-section-title" className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05] text-ink">
              {t.contact.title}
            </h2>
            <p className="text-base sm:text-lg text-ink2 leading-relaxed mt-6 max-w-md">{t.contact.body}</p>

            <div className="mt-10 border-t border-line pt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink3 mb-3">{t.contact.directEmail}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <a href={`mailto:${EMAIL}`} className="font-serif text-2xl sm:text-3xl text-ink hover:text-brass transition-colors duration-300">
                  {EMAIL}
                </a>
                <button
                  data-testid="contact-email-copy-button"
                  onClick={copyEmail}
                  className="inline-flex items-center gap-1.5 border border-linestrong rounded-full px-4 py-1.5 text-xs text-ink2 hover:border-ink hover:text-ink transition-colors duration-300"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? t.contact.copied : t.contact.copy}
                </button>
              </div>
            </div>

            <div className="flex gap-3 mt-10">
              {socials.map(({ id, Icon, url }) => (
                <a
                  key={id}
                  data-testid={`social-link-${id}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={id}
                  className="w-11 h-11 rounded-full border border-linestrong flex items-center justify-center text-ink2 hover:border-brass hover:text-brass transition-colors duration-300"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </motion.div>

          <motion.form
            {...reveal}
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 bg-surface border border-line rounded-sm p-7 sm:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-name" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ink3 mb-2">{t.contact.name}</label>
                <input
                  id="cf-name"
                  data-testid="contact-input-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.contact.namePh}
                  className={fieldCls}
                />
              </div>
              <div>
                <label htmlFor="cf-email" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ink3 mb-2">{t.contact.email}</label>
                <input
                  id="cf-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.contact.emailPh}
                  className={fieldCls}
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="cf-type" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ink3 mb-2">{t.contact.type}</label>
              <select
                id="cf-type"
                data-testid="contact-input-type"
                value={form.project_type}
                onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                className={fieldCls}
              >
                {Object.entries(t.contact.types).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="mt-5">
              <label htmlFor="cf-message" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ink3 mb-2">{t.contact.message}</label>
              <textarea
                id="cf-message"
                data-testid="contact-input-message"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.contact.messagePh}
                className={`${fieldCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-7 inline-flex items-center gap-2 bg-ink text-paper px-8 py-3.5 rounded-full text-sm font-medium tracking-wide hover:bg-brass transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? t.contact.sending : t.contact.submit}
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </motion.form>
        </div>

        <div className="border-t border-line mt-24 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-serif text-xl text-ink">Thomas Lubeau</span>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink3">{t.footer}</p>
        </div>
      </div>
    </section>
  );
};
